import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import { businessMonth, businessRangeQuery, isoToBusinessDate, monthBounds, toBusinessDate } from 'config/time';
import monthlyinspection from '../../assets/icons/driverDetail/monthlysubmissions.svg';
import failedinspection from '../../assets/icons/driverDetail/exceedalcohollimit.svg';
import FailedInspection from 'reusable/FailedInspection';
import MonthNavigator from 'reusable/MonthNavigator';
import Pagination from 'reusable/pagination';
import { PAGE_SIZE } from 'config/constant';
import { pageToRecover } from 'reusable/pageRecovery';
import false_icon from '../../assets/icons/false.svg';
import true_icon from '../../assets/icons/true.svg';
import { useTranslation } from 'react-i18next';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { buttonLike } from 'reusable/buttonLike';

interface VehicleProfileProps {}

interface InspectionRecord {
  id: string;
  create_time: string;
  vehicle: { plate_number: string };
  details: { vehicle_part: VehiclePart[] };
  user: { name: string };
}

interface VehiclePart {
  name: string;
  item: { status: boolean }[];
}

/** ADM-08。当月の初日と末日を業務日で返す。 */
const currentMonthRange = (): [string, string] => {
  const today = toBusinessDate(new Date());
  const [year, month] = today.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const pad = (value: number) => String(value).padStart(2, '0');
  return [`${year}-${pad(month)}-01`, `${year}-${pad(month)}-${pad(lastDay)}`];
};

const VehicleProfile: React.FC<VehicleProfileProps> = () => {
  const { t, i18n } = useTranslation('vehicle');
  const { id } = useParams<{ id: string }>();
  const [vehicleProfile, setVehicleProfile] = useState<InspectionRecord[]>([]);
  const [platnumber, setPlatenumber] = useState('unknown');
  const [vehicleModel, setVehicleModel] = useState('unknown');
  const [monthlyInpection, setMonthlyInspection] = useState(0);
  const [totalInpection, setTotalInspection] = useState(0);
  const [monthlyFailedInpection, setMonthlyFailedInspection] = useState(0);
  const [totalFailedInpection, setTotalFailedInspection] = useState(0);
  // A28（2026-11-03 和田様の UAT）。点検履歴の表の外の月の切り替えで選んだ月（`YYYY-MM`）。
  const [month, setMonth] = useState(businessMonth);
  const [failedInspection, setFailedInspection] = useState<boolean | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  // ADM-15 / RV-044。取得の失敗と、最後の要求の応答だけを反映するための番号。
  // ページや絞り込みを素早く切り替えると、古い応答で一覧を上書きしていた。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();
  const navigate = useNavigate();
  // ADM-08。固定の2024年7月ではなく、当月を対象にする。
  const [startMonth, endMonth] = currentMonthRange();

  const vehicleParts = [
    { en: 'Engine', jp: 'エンジン' },
    { en: 'Lighting/Blinker', jp: '照明装置/方向指示器' },
    { en: 'Tire', jp: 'タイヤ' },
    { en: 'Battery', jp: 'バッテリー' },
    { en: 'Air Tank', jp: 'エアタンク' },
    { en: 'Brake', jp: 'ブレーキ' },
    { en: 'Window Washer Liquid', jp: 'ウインドウォッシャー液' },
    { en: 'Wiper', jp: 'ワイパー' },
  ];

  // 絞り込みを変えたら1ページ目から表示する（後ろのページのままだと空になる）。
  const handleMonthChange = (value: string) => {
    setMonth(value);
    setCurrentPage(1);
  };
  const handleFailedInspectionChange = (value: boolean | null) => {
    setFailedInspection(value);
    setCurrentPage(1);
  };
  const handlePageChange = (value: number) => setCurrentPage(value);

  // ADM-05 / ADM-08。当月は日本時間の業務日の範囲（初日0時〜末日23:59:59.999）で数える。
  // 日付だけで送ると、月初の0〜9時と月末日のほぼ全部が外れていた。
  const monthRange = businessRangeQuery('create_time', startMonth, endMonth);

  const fetchVehicleProfile = useCallback(async () => {
    const isCurrent = beginRequest();
    // ADM-07。一覧は期間・ページ・不合格の絞り込みをサーバーの条件名で送る。A28。期間は選んだ月。
    const listMonth = monthBounds(month);
    let listUrl = `/v1/vehicle_inspection_records/?vehicle=${id}&`;
    listUrl += businessRangeQuery('create_time', listMonth.start, listMonth.end);
    listUrl += `page=${currentPage}&page_size=${PAGE_SIZE}`;
    if (failedInspection !== null) {
      listUrl += `&inspecion_result=${failedInspection}`;
    }
    try {
      const [
        { data: recordsData },
        { data: vehicleData },
        { data: totalInspData },
        { data: monthlyInspData },
        { data: totalFailData },
        { data: monthlyFailData },
      ] = await Promise.all([
        axiosInstance.get(listUrl),
        axiosInstance.get(`/v1/vehicles/${id}/`),
        axiosInstance.get(`/v1/vehicle_inspection_records/?vehicle=${id}`),
        axiosInstance.get(
          `/v1/vehicle_inspection_records/?${monthRange}vehicle=${id}`
        ),
        axiosInstance.get(
          `/v1/vehicle_inspection_records/?vehicle=${id}&inspecion_result=false`
        ),
        axiosInstance.get(
          `/v1/vehicle_inspection_records/?${monthRange}inspecion_result=false&vehicle=${id}`
        ),
      ]);

      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: recordsData.count,
        rows: recordsData.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      setVehicleProfile(recordsData.results);
      setCount(recordsData.count);
      setPlatenumber(vehicleData.plate_number);
      setVehicleModel(vehicleData.car_model);
      setTotalInspection(totalInspData.count);
      setMonthlyInspection(monthlyInspData.count);
      setTotalFailedInspection(totalFailData.count);
      setMonthlyFailedInspection(monthlyFailData.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。点検の一覧のページの外（404）なら1ページ目を読む。車両そのものの 404 などは今までどおり失敗の表示。
      const listFailed = (error as { config?: { url?: string } })?.config?.url === listUrl;
      const recoverPage = listFailed ? pageToRecover(currentPage, PAGE_SIZE, { error }) : null;
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch vehicle profile', error);
      setFetchError(error);
    }
  }, [id, failedInspection, month, currentPage, monthRange, beginRequest]);

  useEffect(() => {
    fetchVehicleProfile();
  }, [fetchVehicleProfile]);

  const handleRowClick = (recordId: string) => navigate(`/vehicle/${recordId}`);

  const checkStatus = (record: InspectionRecord, partName: string) => {
    const part = record.details?.vehicle_part?.find(
      (part) => part.name === partName
    );
    if (!part || part.item.length === 0) return null; // Return null if part or item is not found
    return part.item.every((item) => item.status === true);
  };

  const checkPartStatus = (
    record: InspectionRecord,
    part: { en: string; jp: string }
  ) => checkStatus(record, part.en) ?? checkStatus(record, part.jp);
  return (
    <section className="flex flex-col gap-5 p-4">
      <div>
        <h4 className="text-title font-medium">
          {t('all-vehicle.vehicle-profile.title')}
        </h4>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col justify-center w-1/3 rounded-md bg-white p-4">
          <p className="text-title font-medium">{platnumber}</p>
          <p className="text-gray-600">{vehicleModel}</p>
        </div>
        <div className="flex w-2/3 flex-row rounded-md bg-white">
          <div className="flex-1 p-4 border-r">
            <div className="flex flex-row justify-between">
              <p className="text-title font-medium">{monthlyInpection}</p>
              <img
                src={monthlyinspection}
                alt=""
                className="bg-primary/50 p-2 rounded-full"
              />
            </div>
            <div className="flex flex-col">
              <p>
                {t('all-vehicle.vehicle-profile.status.monthly-inspection')}
              </p>
              <p className="text-gray-600">
                {t('all-vehicle.vehicle-profile.status.monthly-inspection-info', {
                  total: totalInpection,
                })}
              </p>
            </div>
          </div>
          <div className="flex-1 p-4">
            <div className="flex flex-row justify-between">
              <p className="text-title font-medium">{monthlyFailedInpection}</p>
              <img
                src={failedinspection}
                alt=""
                className="bg-primary/50 p-2 rounded-full"
              />
            </div>
            <div>
              <p>{t('all-vehicle.vehicle-profile.status.failed-inspection')}</p>
              <p className="text-gray-600">
                {t('all-vehicle.vehicle-profile.status.failed-inspection-info', {
                  total: totalFailedInpection,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col bg-white rounded-md p-4 gap-4">
        <div className="flex flex-row justify-between">
          <h4 className="text-subtitle-1 font-medium">
            {t('all-vehicle.vehicle-profile.table.title')}
          </h4>
          <div className="flex flex-row gap-4 items-center">
            <MonthNavigator month={month} onChange={handleMonthChange} />
            <FailedInspection onChange={handleFailedInspectionChange} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4 items-center">
            <h6 className="w-2/12 whitespace-nowrap">
              {t('all-vehicle.vehicle-profile.table.inspection-date')}
            </h6>
            <h6 className="w-1/12">
              {t('all-vehicle.vehicle-profile.table.plate-number')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.engine')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.lighting')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.tire')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.battery')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.air-tank')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.brake')}
            </h6>
            {/* A28。列名は短く「WW液」。正式な名前は title と読み上げで伝える。 */}
            <h6 className="w-1/12 text-center whitespace-nowrap">
              <abbr title={t('all-vehicle.vehicle-profile.table.window-washer-liquid-full')} className="no-underline">
                {t('all-vehicle.vehicle-profile.table.window-washer-liquid')}
              </abbr>
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.wiper')}
            </h6>
            <h6 className="w-2/12 text-center">
              {t('all-vehicle.vehicle-profile.table.inspection-by')}
            </h6>
            <h6 className="w-1/12 text-center">
              {t('all-vehicle.vehicle-profile.table.details')}
            </h6>
          </div>
          {fetchError ? (
            <FetchFailed error={fetchError} onRetry={fetchVehicleProfile} />
          ) : (
            <div className="px-4">
              {vehicleProfile.map((record) => (
                <div
                  key={record.id}
                  className="flex flex-row items-center justify-between border-b cursor-pointer"
                  {...buttonLike(() => handleRowClick(record.id))}
                >
                  {/* A28。日付は折らない。 */}
                  <p className="w-2/12 py-3 whitespace-nowrap">
                    {isoToBusinessDate(record.create_time)}
                  </p>
                  <p className="w-1/12 text-left">
                    {record.vehicle.plate_number}
                  </p>
                  {vehicleParts.map((part) => {
                    const status = checkPartStatus(record, part);
                    return (
                      <p key={part.en} className="flex w-1/12 justify-center">
                        {status === null || status === undefined ? (
                          '-' // Display "-" if no value is present
                        ) : (
                          <img
                            className="h-7"
                            src={status ? true_icon : false_icon}
                            alt={status ? t('ui.check-ok', { ns: 'common' }) : t('ui.check-ng', { ns: 'common' })}
                          />
                        )}
                      </p>
                    );
                  })}
                  <p className="w-2/12 text-center">{record.user.name}</p>
                  <p className="w-1/12 text-center underline text-blue-600">
                    {t('all-vehicle.vehicle-profile.table.detail_button')}
                  </p>
                </div>
              ))}
            </div>
          )}
          <Pagination
            allRows={count}
            rowsPerPage={PAGE_SIZE}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </section>
  );
};

export default VehicleProfile;